'use client';

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Store, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { useDebounce } from '@/app/hooks/useDebounce';
import SearchableSelect, {
  type SearchableOption,
} from '@/app/features/admin/components/edi-admin/SearchableSelect';
import { fetchMyBuyers, fetchSuppliersForBuyer } from '@/app/store/thunks/activePartyThunks';
import { setActiveBuyer, setActiveSupplier } from '@/app/store/slices/activePartySlice';
import {
  selectActiveBuyer,
  selectActiveSupplier,
  selectMyBuyers,
  selectSuppliersForActiveBuyer,
  selectActivePartyIsUnrestricted,
  selectActivePartyIsLoadingBuyers,
  selectActivePartyIsLoadingSuppliers,
  selectActivePartyLoadedForTenant,
  selectActivePartyError,
  selectMyEdiDirection,
  selectMyEdiHasGlobalRead,
  selectMySupplierParties,
} from '@/app/store/selectors/activePartySelectors';
import {
  readPersistedBuyerId,
  readPersistedSupplierId,
} from '@/app/store/slices/activePartyStorage';
import { ALL_PARTY_ID, type MyPartyItem } from '@/app/services/api/activePartyService';

const toOption = (p: MyPartyItem): SearchableOption => ({
  value: String(p.partyId),
  primary: p.partyName,
  secondary: p.partyCode,
  keywords: `${p.partyName} ${p.partyCode}`.toLowerCase(),
});

/**
 * Workspace top-bar active-party selector (spec: edi-admin-and-scoped-party-selector).
 *
 * Direction-aware: the user's OWNED axis (buyer for buyer-roles, supplier for
 * supplier-roles) is locked/limited to their assigned party(ies) and required;
 * the OTHER axis offers an explicit "All Buyers"/"All Suppliers" + any specific.
 * EDI-module-wide users (edi.admin / tenant / sys admin) get "All" on BOTH axes.
 *
 * "All" on the non-owned axis is represented as a null active party — the shipped
 * per-party isolation reads null as "no filter on that axis", bounded by the
 * authorized owned axis (or global EDI access). The owned axis is always set, so
 * an empty owned axis is never "all". Also acts as the loader for buyers/suppliers.
 */
function ActivePartySelectorImpl() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('activeParty');

  const activeTenant = useAppSelector((s) => s.tenant.activeTenant);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const isInitialized = useAppSelector((s) => s.auth.isInitialized);

  const persistedBuyerPref = useAppSelector(
    (s) => s.userPreferences.preferences?.activeBuyerPartyId ?? null
  );
  const persistedSupplierPref = useAppSelector(
    (s) => s.userPreferences.preferences?.activeSupplierPartyId ?? null
  );
  const preferencesSettled = useAppSelector(
    (s) => s.userPreferences.preferences !== null || s.userPreferences.error !== null
  );

  const activeBuyer = useAppSelector(selectActiveBuyer);
  const activeSupplier = useAppSelector(selectActiveSupplier);
  const buyers = useAppSelector(selectMyBuyers);
  const suppliers = useAppSelector(selectSuppliersForActiveBuyer);
  const isUnrestricted = useAppSelector(selectActivePartyIsUnrestricted);
  const isLoadingBuyers = useAppSelector(selectActivePartyIsLoadingBuyers);
  const isLoadingSuppliers = useAppSelector(selectActivePartyIsLoadingSuppliers);
  const loadedForTenantId = useAppSelector(selectActivePartyLoadedForTenant);
  const error = useAppSelector(selectActivePartyError);

  // ── My EDI scope → selector mode (spec: edi-admin-and-scoped-party-selector) ──
  const direction = useAppSelector(selectMyEdiDirection);
  const hasGlobalEdiRead = useAppSelector(selectMyEdiHasGlobalRead);
  const mySupplierParties = useAppSelector(selectMySupplierParties);

  const buyerOwned = direction === 'buyer' || direction === 'hybrid';
  const supplierOwned = direction === 'supplier' || direction === 'hybrid';
  // "All" is offered on the non-owned axis of a party-scoped user, or both axes
  // for a global EDI user. NEVER on a party-scoped user's owned axis.
  const canAllBuyers = hasGlobalEdiRead || direction === 'supplier';
  const canAllSuppliers = hasGlobalEdiRead || direction === 'buyer';

  const tenantId = activeTenant?.tenantId ? Number(activeTenant.tenantId) : null;
  // Only a SPECIFIC buyer (>0) drives the supplier chain; null = "All Buyers".
  const specificBuyerId = activeBuyer && activeBuyer.partyId > 0 ? activeBuyer.partyId : null;

  const [open, setOpen] = useState(false);
  const [buyerQuery, setBuyerQuery] = useState('');
  const debouncedBuyerQuery = useDebounce(buyerQuery, 300);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const supplierRestoreRef = useRef(true);

  // --- Loader: buyer list once auth + tenant are ready (unchanged).
  useEffect(() => {
    if (!isAuthenticated || !isInitialized || tenantId == null) return;
    if (loadedForTenantId === tenantId) return;

    const fromStorage = readPersistedBuyerId(tenantId);
    if (fromStorage !== null) {
      dispatch(fetchMyBuyers({ tenantId, persistedBuyerId: fromStorage }));
      return;
    }
    if (!preferencesSettled) return;
    dispatch(fetchMyBuyers({ tenantId, persistedBuyerId: persistedBuyerPref }));
  }, [
    isAuthenticated,
    isInitialized,
    tenantId,
    loadedForTenantId,
    persistedBuyerPref,
    preferencesSettled,
    dispatch,
  ]);

  // --- Server-side buyer search for the (large) global/internal list.
  useEffect(() => {
    if (!isUnrestricted || tenantId == null) return;
    dispatch(fetchMyBuyers({ tenantId, searchText: debouncedBuyerQuery.trim() || undefined }));
  }, [debouncedBuyerQuery, isUnrestricted, tenantId, dispatch]);

  useEffect(() => {
    setBuyerQuery('');
    supplierRestoreRef.current = true;
  }, [tenantId]);

  // --- Supplier chain: load suppliers for a SPECIFIC active buyer — but NOT when
  //     the supplier is the user's owned axis (then it comes from `mySupplierParties`).
  useEffect(() => {
    if (supplierOwned) return;
    if (tenantId == null || specificBuyerId == null) return;
    let persistedSupplierId: number | null = null;
    if (supplierRestoreRef.current) {
      supplierRestoreRef.current = false;
      persistedSupplierId = readPersistedSupplierId(tenantId) ?? persistedSupplierPref;
    }
    dispatch(
      fetchSuppliersForBuyer({ tenantId, buyerPartyId: specificBuyerId, persistedSupplierId })
    );
  }, [tenantId, specificBuyerId, supplierOwned, persistedSupplierPref, dispatch]);

  // --- Owned supplier auto-set: a supplier-role user's supplier is their assigned
  //     party — set it (so the query carries it) independent of the buyer, even when
  //     buyer = "All Buyers". Sole supplier locks; with several the user picks.
  useEffect(() => {
    if (!supplierOwned || activeSupplier != null) return;
    if (mySupplierParties.length === 1) dispatch(setActiveSupplier(mySupplierParties[0]));
  }, [supplierOwned, activeSupplier, mySupplierParties, dispatch]);

  // Close the popover on an outside click.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // ── Option lists per axis ──────────────────────────────────────────────────
  // Owned supplier axis sources from mySupplierParties; otherwise the chained list.
  const supplierSource = supplierOwned ? mySupplierParties : suppliers;

  const ALL = String(ALL_PARTY_ID);
  const buyerOptions = useMemo(() => {
    const opts = buyers.map(toOption);
    return canAllBuyers
      ? [{ value: ALL, primary: t('buyer.all'), keywords: 'all buyers' }, ...opts]
      : opts;
  }, [buyers, canAllBuyers, t, ALL]);
  const supplierOptions = useMemo(() => {
    const opts = supplierSource.map(toOption);
    return canAllSuppliers
      ? [{ value: ALL, primary: t('supplier.all'), keywords: 'all suppliers' }, ...opts]
      : opts;
  }, [supplierSource, canAllSuppliers, t, ALL]);

  const onBuyerChange = useCallback(
    (value: string) => {
      const id = Number(value);
      supplierRestoreRef.current = false;
      dispatch(setActiveBuyer(id === ALL_PARTY_ID ? null : buyers.find((b) => b.partyId === id) ?? null));
    },
    [buyers, dispatch]
  );

  const onSupplierChange = useCallback(
    (value: string) => {
      const id = Number(value);
      dispatch(
        setActiveSupplier(id === ALL_PARTY_ID ? null : supplierSource.find((s) => s.partyId === id) ?? null)
      );
    },
    [supplierSource, dispatch]
  );

  // ── Display values ─────────────────────────────────────────────────────────
  const buyerSelectValue = activeBuyer ? String(activeBuyer.partyId) : canAllBuyers ? ALL : '';
  const supplierSelectValue = activeSupplier ? String(activeSupplier.partyId) : canAllSuppliers ? ALL : '';

  const buyerSummary = activeBuyer ? activeBuyer.partyName : canAllBuyers ? t('buyer.all') : null;
  const supplierSummary = activeSupplier ? activeSupplier.partyName : canAllSuppliers ? t('supplier.all') : null;
  const summary =
    [buyerSummary, supplierSummary].filter(Boolean).join(' · ') || t('selector.placeholder');

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('selector.ariaLabel')}
        aria-expanded={open}
        title={summary}
        className={cn(
          'inline-flex items-center justify-center h-8 w-8 md:w-auto md:px-3 md:justify-start',
          'gap-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10',
          'md:text-xs md:font-medium transition-colors whitespace-nowrap'
        )}
      >
        <Store size={16} className="shrink-0" />
        <span className="hidden md:inline max-w-[16rem] truncate">{summary}</span>
        <ChevronDown size={14} className="hidden md:inline shrink-0 opacity-70" />
      </button>

      {open && (
        <div
          className={cn(
            'absolute right-0 z-[1000000] mt-2 w-80 rounded-md p-3 shadow-lg',
            'border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
            'text-gray-900 dark:text-gray-100'
          )}
        >
          {error && <p className="mb-2 text-xs text-red-600 dark:text-red-400">{t('error')}</p>}

          {/* Buyer axis */}
          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">{t('buyer.label')}</label>
            {buyerOwned && buyers.length === 1 ? (
              // Owned + single → locked (required, no clear, no "All").
              <FixedPartyChip party={buyers[0]} />
            ) : (
              <SearchableSelect
                id="active-buyer"
                value={buyerSelectValue}
                options={buyerOptions}
                placeholder={buyerOwned ? t('buyer.selectRequired') : t('buyer.placeholder')}
                searchPlaceholder={t('buyer.search')}
                emptyText={t('buyer.empty')}
                noMatchText={t('buyer.noMatch')}
                loading={isLoadingBuyers}
                loadingText={t('buyer.loading')}
                onChange={onBuyerChange}
                onQueryChange={isUnrestricted ? setBuyerQuery : undefined}
              />
            )}
          </div>

          {/* Supplier axis */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">{t('supplier.label')}</label>
            {supplierOwned && mySupplierParties.length === 1 ? (
              <FixedPartyChip party={mySupplierParties[0]} />
            ) : !supplierOwned && !canAllSuppliers && !activeBuyer ? (
              // Non-owned supplier with no "All" affordance still needs a buyer first.
              <p className="px-3 py-2 text-xs text-muted-foreground border border-dashed border-gray-200 dark:border-gray-700 rounded-md">
                {t('supplier.pickBuyerFirst')}
              </p>
            ) : (
              <SearchableSelect
                id="active-supplier"
                value={supplierSelectValue}
                options={supplierOptions}
                placeholder={supplierOwned ? t('supplier.selectRequired') : t('supplier.placeholder')}
                searchPlaceholder={t('supplier.search')}
                emptyText={t('supplier.empty')}
                noMatchText={t('supplier.noMatch')}
                loading={isLoadingSuppliers}
                loadingText={t('supplier.loading')}
                onChange={onSupplierChange}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Read-only display for a locked single-option axis (the owned-axis common case). */
function FixedPartyChip({ party }: { party: MyPartyItem }) {
  return (
    <div className="flex h-9 items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 text-sm">
      <span className="truncate">{party.partyName}</span>
      <span className="text-xs text-muted-foreground truncate">{party.partyCode}</span>
    </div>
  );
}

export const ActivePartySelector = React.memo(ActivePartySelectorImpl);
ActivePartySelector.displayName = 'ActivePartySelector';

export default ActivePartySelector;
